import React from 'react';
import { Shield, Eye, Lock, UserCheck, Trash2, Mail, Database, Cookie } from 'lucide-react';

const PrivacyPage = () => {
    return (
        <div className="min-h-screen bg-background">
            <section className="relative py-24 bg-primary text-white">
                <div className="container-main">
                    <h1 className="font-serif text-4xl md:text-5xl font-bold">Политика за поверителност</h1>
                    <p className="mt-4 text-white/80">Последна актуализация: Април 2026 г.</p>
                </div>
            </section>

            <section className="section-spacing bg-white">
                <div className="container-main">
                    <div className="max-w-4xl mx-auto">
                        
                        {/* Data Controller Info */}
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
                            <h2 className="font-serif text-xl font-bold text-blue-900 mb-3 flex items-center gap-2">
                                <Shield className="w-5 h-5" />
                                Администратор на лични данни
                            </h2>
                            <div className="text-sm text-blue-800">
                                <p><strong>Фирма:</strong> ВКУСЪТ НА ТУРЦИЯ ЕООД (VKUSAT NA TURKEY EOOD)</p>
                                <p><strong>ЕИК:</strong> 208822640</p>
                                <p><strong>Адрес:</strong> гр. София, ул. Осогово 60</p>
                                <p><strong>Имейл:</strong> vkustnaturciq39@gmail.com</p>
                                <p><strong>Телефон:</strong> 0899 97 27 27</p>
                            </div>
                        </div>

                        <div className="prose prose-lg max-w-none">
                            
                            {/* Introduction */}
                            <div className="mb-8">
                                <h2 className="font-serif text-2xl font-bold">Въведение</h2>
                                <p className="text-muted-foreground">
                                    ВКУСЪТ НА ТУРЦИЯ ЕООД (VKUSAT NA TURKEY EOOD) (наричано по-нататък „Ние" или „Дружеството") се ангажира да защитава 
                                    поверителността на вашите лични данни в съответствие с Общия регламент за защита на данните 
                                    (GDPR - Регламент (ЕС) 2016/679) и Закона за защита на личните данни.
                                </p>
                                <p className="text-muted-foreground">
                                    Тази политика описва как събираме, използваме, съхраняваме и защитаваме вашите лични данни 
                                    при използване на онлайн магазина vkusutnaturcia.bg.
                                </p>
                            </div>

                            {/* What data we collect */}
                            <div className="mb-8">
                                <h2 className="font-serif text-2xl font-bold flex items-center gap-2">
                                    <Database className="w-6 h-6 text-amber-600" />
                                    Какви данни събираме
                                </h2>
                                <p className="text-muted-foreground">При регистрация и поръчка събираме:</p>
                                <ul className="text-muted-foreground list-disc pl-6">
                                    <li><strong>Идентификационни данни:</strong> име, фамилия</li>
                                    <li><strong>Данни за контакт:</strong> имейл адрес, телефонен номер</li>
                                    <li><strong>Адресни данни:</strong> адрес за доставка, град, пощенски код</li>
                                    <li><strong>Данни за поръчки:</strong> история на покупките, предпочитания</li>
                                    <li><strong>Технически данни:</strong> IP адрес, тип браузър, операционна система</li>
                                </ul>
                                <p className="text-muted-foreground mt-4">
                                    <strong>Важно:</strong> Ние НЕ събираме и НЕ съхраняваме данни за банкови карти. 
                                    Плащанията се обработват изцяло от сертифицирания PCI DSS доставчик Stripe.
                                </p>
                            </div>

                            {/* Legal basis */}
                            <div className="mb-8">
                                <h2 className="font-serif text-2xl font-bold flex items-center gap-2">
                                    <Eye className="w-6 h-6 text-amber-600" />
                                    Правно основание за обработка
                                </h2>
                                <p className="text-muted-foreground">Обработваме личните ви данни на следните основания:</p>
                                <ul className="text-muted-foreground list-disc pl-6">
                                    <li><strong>Изпълнение на договор</strong> - за обработка на поръчки и доставка на продукти</li>
                                    <li><strong>Законово задължение</strong> - за издаване на фактури и счетоводна отчетност</li>
                                    <li><strong>Легитимен интерес</strong> - за подобряване на услугите и сигурност</li>
                                    <li><strong>Съгласие</strong> - за маркетингови съобщения (само ако сте се съгласили)</li>
                                </ul>
                            </div>

                            {/* How we use data */}
                            <div className="mb-8">
                                <h2 className="font-serif text-2xl font-bold">Как използваме данните</h2>
                                <ul className="text-muted-foreground list-disc pl-6">
                                    <li>Обработка и изпълнение на поръчки</li>
                                    <li>Комуникация относно статус на поръчки и доставки</li>
                                    <li>Издаване на фактури и счетоводни документи</li>
                                    <li>Отговор на запитвания и рекламации</li>
                                    <li>Подобряване на потребителското изживяване</li>
                                    <li>Маркетингови съобщения (само с ваше съгласие)</li>
                                    <li>Предотвратяване на измами и злоупотреби</li>
                                </ul>
                            </div>

                            {/* Data sharing */}
                            <div className="mb-8">
                                <h2 className="font-serif text-2xl font-bold">Споделяне на данни с трети страни</h2>
                                <p className="text-muted-foreground">Споделяме данни само със следните категории получатели:</p>
                                <ul className="text-muted-foreground list-disc pl-6">
                                    <li><strong>Еконт Експрес ООД</strong> - за доставка на поръчки (име, адрес, телефон)</li>
                                    <li><strong>Stripe, Inc.</strong> - за обработка на картови плащания</li>
                                    <li><strong>Държавни органи</strong> - при законово изискване (НАП, съд)</li>
                                </ul>
                                <p className="text-muted-foreground mt-2">
                                    НЕ продаваме и НЕ предоставяме личните ви данни на трети страни за маркетингови цели.
                                </p>
                            </div>

                            {/* Data retention */}
                            <div className="mb-8">
                                <h2 className="font-serif text-2xl font-bold">Срок на съхранение</h2>
                                <ul className="text-muted-foreground list-disc pl-6">
                                    <li><strong>Данни за акаунт:</strong> до изтриване на акаунта</li>
                                    <li><strong>Данни за поръчки:</strong> 5 години (счетоводни изисквания)</li>
                                    <li><strong>Данни за фактури:</strong> 10 години (данъчни изисквания)</li>
                                    <li><strong>Маркетингово съгласие:</strong> до оттегляне</li>
                                </ul>
                            </div>

                            {/* Your rights */}
                            <div className="mb-8 p-6 bg-green-50 border border-green-200 rounded-xl">
                                <h2 className="font-serif text-2xl font-bold text-green-800 flex items-center gap-2">
                                    <UserCheck className="w-6 h-6" />
                                    Вашите права по GDPR
                                </h2>
                                <p className="text-green-700 mt-2">Имате следните права относно личните си данни:</p>
                                <ul className="text-green-700 list-disc pl-6 mt-2">
                                    <li><strong>Право на достъп</strong> - да получите копие от вашите данни</li>
                                    <li><strong>Право на коригиране</strong> - да поправите неточни данни</li>
                                    <li><strong>Право на изтриване</strong> ("право да бъдеш забравен") - да поискате изтриване</li>
                                    <li><strong>Право на ограничаване</strong> - да ограничите обработката</li>
                                    <li><strong>Право на преносимост</strong> - да получите данните в машинночетим формат</li>
                                    <li><strong>Право на възражение</strong> - да възразите срещу обработката</li>
                                    <li><strong>Право на оттегляне на съгласие</strong> - по всяко време</li>
                                </ul>
                                <p className="text-green-700 mt-4">
                                    За упражняване на правата си, моля свържете се с нас на: <strong>vkustnaturciq39@gmail.com</strong>
                                </p>
                                <p className="text-green-700 mt-2">
                                    Имате право да подадете жалба до <strong>Комисия за защита на личните данни (КЗЛД)</strong>:{' '}
                                    <a href="https://www.cpdp.bg" target="_blank" rel="noopener noreferrer" className="underline">
                                        www.cpdp.bg
                                    </a>
                                </p>
                            </div>

                            {/* Cookies */}
                            <div className="mb-8">
                                <h2 className="font-serif text-2xl font-bold flex items-center gap-2">
                                    <Cookie className="w-6 h-6 text-amber-600" />
                                    Бисквитки (Cookies)
                                </h2>
                                <p className="text-muted-foreground">Използваме следните видове бисквитки:</p>
                                <ul className="text-muted-foreground list-disc pl-6">
                                    <li><strong>Необходими</strong> - за работа на сайта (сесия, кошница, вход)</li>
                                    <li><strong>Функционални</strong> - за запомняне на предпочитания</li>
                                    <li><strong>Аналитични</strong> - за разбиране как използвате сайта (само със съгласие)</li>
                                    <li><strong>Маркетингови</strong> - за персонализирана реклама (само със съгласие)</li>
                                </ul>
                                <p className="text-muted-foreground mt-2">
                                    Можете да управлявате бисквитките чрез банера при първо посещение или чрез настройките на браузъра.
                                </p>
                            </div>

                            {/* Security */}
                            <div className="mb-8">
                                <h2 className="font-serif text-2xl font-bold flex items-center gap-2">
                                    <Lock className="w-6 h-6 text-amber-600" />
                                    Сигурност на данните
                                </h2>
                                <p className="text-muted-foreground">
                                    Прилагаме следните мерки за защита на вашите данни:
                                </p>
                                <ul className="text-muted-foreground list-disc pl-6">
                                    <li>SSL/TLS криптиране на всички комуникации</li>
                                    <li>Криптирани пароли (bcrypt хеширане)</li>
                                    <li>Ограничен достъп до лични данни</li>
                                    <li>Редовни актуализации на сигурността</li>
                                    <li>PCI DSS съвместимост за плащания (чрез Stripe)</li>
                                </ul>
                            </div>

                            {/* Contact */}
                            <div className="mb-8">
                                <h2 className="font-serif text-2xl font-bold flex items-center gap-2">
                                    <Mail className="w-6 h-6 text-amber-600" />
                                    Контакт за защита на данни
                                </h2>
                                <p className="text-muted-foreground">
                                    За въпроси относно защитата на лични данни:
                                </p>
                                <ul className="text-muted-foreground list-disc pl-6">
                                    <li><strong>Имейл:</strong> vkustnaturciq39@gmail.com</li>
                                    <li><strong>Телефон:</strong> 0899 97 27 27</li>
                                    <li><strong>Адрес:</strong> гр. София, ул. Осогово 60</li>
                                </ul>
                                <p className="text-muted-foreground mt-4">
                                    Ще отговорим на вашето запитване в срок до 30 дни.
                                </p>
                            </div>

                            {/* Changes */}
                            <div className="mb-8">
                                <h2 className="font-serif text-2xl font-bold">Промени в политиката</h2>
                                <p className="text-muted-foreground">
                                    Запазваме правото си да актуализираме тази политика. При съществени промени ще ви уведомим 
                                    чрез имейл или съобщение на сайта. Датата на последна актуализация е посочена в началото.
                                </p>
                            </div>

                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default PrivacyPage;
